import { CompletionTargetAudienceEnum } from '../../../api/ai/enums/completion-target-audience.enum';
import { CompletionLengthEnum } from '../../../api/ai/enums/completion-length.enum';
import { CoverageInsightsApi } from '../../../api/ai/model/coverage-insights-api.model';

import { promptLanguageOptions } from '../../../Common/Forms/data/prompt-language-options';
import { promptLengthOptions, sentimentOpinionLengthOptions } from '../../../Common/Forms/data/prompt-length-options';
import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';
import { AiAnalysisTypes } from '../../../Common/Mentions/enums/ai-analysis-types.enum';

import { isEqual } from 'lodash';
import { ContentTypeEnum } from '../../../api/ai/enums/content-type.enum';
import { GoalTypeEnum } from '../../../api/ai/enums/goal-type.enum';
import { CompletionToneEnum } from '../../../api/ai/enums/completion-tone.enum';

export class CoverageInsights {
    aiAnalysisType: AiAnalysisTypes;
    adHocCustomPrompt?: string;

    focus?: string;
    keyTakeawayHeading?: string;

    numOfTakeaways?: number;
    brand?: string;
    context?: string;

    selectedLengthOption: DropdownOptionModel;
    lengthOptions: DropdownOptionModel[] = promptLengthOptions;

    selectedLanguageOption: DropdownOptionModel;
    languageOptions = promptLanguageOptions;
    selectedLanguageTag?: string;

    customPromptId?: number;

    userDefinedKeyMessage?: string;
    keyMessageTitle?: string;
    keyMessageSummary?: string;
    keywords?: string[];
    includeOutletReference?: boolean = true;
    numberKeyMessages?: number;
    numberMentions?: number;
    enableDuplicateMentions?: boolean;
    includeStrategy?: boolean;
    includeSentiment?: boolean;
    includeThemes?: boolean;
    increased?: boolean;

    representation?: string;
    contentType?: ContentTypeEnum;
    targetAudience?: CompletionTargetAudienceEnum;
    goal?: GoalTypeEnum;
    completionTone?: CompletionToneEnum;

    customContentType?: string;
    position?: string;
    customGoal?: string;
    customTargetAudience?: string;
    customTone?: string;
    customLanguage?: string;

    constructor(apiModel?: CoverageInsightsApi) {
        if (apiModel) {
            if (apiModel.bulkAnalysisMessage) {

                this.aiAnalysisType = apiModel.bulkAnalysisMessage.aiAnalysisType;
                if (this.aiAnalysisType === AiAnalysisTypes.SENTIMENT_OPINION) {
                    this.lengthOptions = sentimentOpinionLengthOptions;
                }
                if (apiModel.bulkAnalysisMessage.adHocCustomPrompt) {
                    this.adHocCustomPrompt = apiModel.bulkAnalysisMessage.adHocCustomPrompt;
                }

                if (apiModel.bulkAnalysisMessage.customPromptId) {
                    this.customPromptId = apiModel.bulkAnalysisMessage.customPromptId;
                } else {
                    this.focus = apiModel.bulkAnalysisMessage.focus;
                    this.keyTakeawayHeading = apiModel.bulkAnalysisMessage.keyTakeawayHeading;
                    this.selectedLengthOption = this.lengthOptions.find(option => option.value === apiModel.bulkAnalysisMessage.length);
                    this.numOfTakeaways = apiModel.bulkAnalysisMessage.numOfTakeaways;
                    this.brand = apiModel.bulkAnalysisMessage.brand;
                    this.context = apiModel.bulkAnalysisMessage.context;
                    this.userDefinedKeyMessage = apiModel.bulkAnalysisMessage.userDefinedKeyMessage;
                    this.keyMessageTitle = apiModel.bulkAnalysisMessage.keyMessageTitle;
                    this.keyMessageSummary = apiModel.bulkAnalysisMessage.keyMessageSummary;
                    this.keywords = apiModel.bulkAnalysisMessage.keywords;
                    this.includeOutletReference = apiModel.bulkAnalysisMessage.includeOutletReference;
                    this.numberKeyMessages = apiModel.bulkAnalysisMessage.numberKeyMessages;
                    this.numberMentions = apiModel.bulkAnalysisMessage.numberMentions;
                    this.includeStrategy = apiModel.bulkAnalysisMessage.includeStrategy;
                    this.includeSentiment = apiModel.bulkAnalysisMessage.includeSentiment;
                    this.includeThemes = apiModel.bulkAnalysisMessage.includeThemes;
                    this.increased = apiModel.bulkAnalysisMessage.increased;
                    this.representation = apiModel.bulkAnalysisMessage.representation;
                    this.contentType = apiModel.bulkAnalysisMessage.contentType;
                    this.targetAudience = apiModel.bulkAnalysisMessage.targetAudience;
                    this.goal = apiModel.bulkAnalysisMessage.goal;
                    this.completionTone = apiModel.bulkAnalysisMessage.completionTone;
                    this.customContentType = apiModel.bulkAnalysisMessage.customContentType;
                    this.position = apiModel.bulkAnalysisMessage.position;
                    this.customGoal = apiModel.bulkAnalysisMessage.customGoal;
                    this.customTargetAudience = apiModel.bulkAnalysisMessage.customTargetAudience;
                    this.customTone = apiModel.bulkAnalysisMessage.customTone;
                    this.customLanguage = apiModel.bulkAnalysisMessage.customLanguage;
                }

                this.selectedLanguageOption = this.languageOptions.find(option => option.value === apiModel.bulkAnalysisMessage.languageTag);
                this.selectedLanguageTag = this.selectedLanguageOption?.value;
            }
        } else {
            this.selectedLengthOption = this.lengthOptions.find(option => option.value === CompletionLengthEnum.MEDIUM);
            this.selectedLanguageOption = this.languageOptions.find(option => option.value === 'en-US');
            this.selectedLanguageTag = this.selectedLanguageOption?.value;
        }
    }

    toApiModel(): CoverageInsightsApi {
        const apiModel: CoverageInsightsApi = {
            bulkAnalysisMessage: {
                aiAnalysisType: this.aiAnalysisType
            }
        };

        if (this.adHocCustomPrompt) {
            apiModel.bulkAnalysisMessage.adHocCustomPrompt = this.adHocCustomPrompt;
        }

        if (this.customPromptId) {
            apiModel.bulkAnalysisMessage.customPromptId = this.customPromptId;
        } else {
            if (this.selectedLengthOption?.value) {
                apiModel.bulkAnalysisMessage.length = this.selectedLengthOption?.value;
            }

            apiModel.bulkAnalysisMessage.focus = this.focus?.slice(0, 255);
            apiModel.bulkAnalysisMessage.keyTakeawayHeading = this.keyTakeawayHeading;
            apiModel.bulkAnalysisMessage.numOfTakeaways = this.numOfTakeaways;
            apiModel.bulkAnalysisMessage.brand = this.brand;
            apiModel.bulkAnalysisMessage.context = this.context;
            apiModel.bulkAnalysisMessage.userDefinedKeyMessage = this.userDefinedKeyMessage;
            apiModel.bulkAnalysisMessage.keyMessageTitle = this.keyMessageTitle;
            apiModel.bulkAnalysisMessage.keyMessageSummary = this.keyMessageSummary;
            apiModel.bulkAnalysisMessage.keywords = this.keywords;
            apiModel.bulkAnalysisMessage.includeOutletReference = this.includeOutletReference;
            apiModel.bulkAnalysisMessage.numberMentions = this.numberMentions;
            apiModel.bulkAnalysisMessage.numberKeyMessages = this.numberKeyMessages;
            apiModel.bulkAnalysisMessage.enableDuplicateMentions = this.enableDuplicateMentions;
            apiModel.bulkAnalysisMessage.includeStrategy = this.includeStrategy;
            apiModel.bulkAnalysisMessage.includeSentiment = this.includeSentiment;
            apiModel.bulkAnalysisMessage.includeThemes = this.includeThemes;
            apiModel.bulkAnalysisMessage.increased = this.increased;
            apiModel.bulkAnalysisMessage.representation = this.representation;
            apiModel.bulkAnalysisMessage.contentType = this.contentType;
            apiModel.bulkAnalysisMessage.goal = this.goal;
            apiModel.bulkAnalysisMessage.completionTone = this.completionTone;
            apiModel.bulkAnalysisMessage.customContentType = this.customContentType;
            apiModel.bulkAnalysisMessage.position = this.position;
            apiModel.bulkAnalysisMessage.customGoal = this.customGoal;
            apiModel.bulkAnalysisMessage.customTargetAudience = this.customTargetAudience;
            apiModel.bulkAnalysisMessage.customTone = this.customTone;
            if (this.customLanguage) {
                apiModel.bulkAnalysisMessage.customLanguage = this.customLanguage;
            } else {
                if (this.selectedLanguageOption?.value) {
                    apiModel.bulkAnalysisMessage.languageTag = this.selectedLanguageOption?.value;
                } else if (this.selectedLanguageTag) {
                    apiModel.bulkAnalysisMessage.languageTag = this.selectedLanguageTag;
                }
            }
        }
        return apiModel;
    }

    clone(): CoverageInsights {
        return new CoverageInsights(this.toApiModel());
    }

    equals(other: CoverageInsights): boolean {
        if (this.aiAnalysisType !== other.aiAnalysisType) {
            return false;
        } else if (this.aiAnalysisType === AiAnalysisTypes.COLLECTION_CUSTOM) {
            if (this.adHocCustomPrompt !== other.adHocCustomPrompt) {
                return false;
            }
        }
        if (this.focus !== other.focus) {
            return false;
        }
        if (this.keyTakeawayHeading !== other.keyTakeawayHeading) {
            return false;
        }
        if (this.selectedLengthOption?.value !== other.selectedLengthOption?.value) {
            return false;
        }
        if (this.selectedLanguageOption?.value !== other.selectedLanguageOption?.value) {
            return false;
        }
        if (this.customPromptId !== other.customPromptId) {
            return false;
        }
        if (this.numOfTakeaways !== other.numOfTakeaways) {
            return false;
        }
        if (this.brand !== other.brand) {
            return false;
        }
        if (this.context !== other.context) {
            return false;
        }
        if (this.userDefinedKeyMessage !== other.userDefinedKeyMessage) {
            return false;
        }
        if (this.keyMessageTitle !== other.keyMessageTitle) {
            return false;
        }
        if (this.keyMessageSummary !== other.keyMessageSummary) {
            return false;
        }
        if (!isEqual(this.keywords, other.keywords)) {
            return false;
        }

        if (this.includeOutletReference !== other.includeOutletReference) {
            return false;
        }
        if (this.numberKeyMessages !== other.numberKeyMessages) {
            return false;
        }

        if (this.numberMentions !== other.numberMentions) {
            return false;
        }
        if (this.includeSentiment !== other.includeSentiment) {
            return false;
        }
        if (this.includeStrategy !== other.includeStrategy) {
            return false;
        }
        if (this.includeThemes !== other.includeThemes) {
            return false;
        }

        return true;
    }

    setLengthOptionByValue(value: CompletionLengthEnum): void {
        this.selectedLengthOption = this.lengthOptions.find(option => option.value === value);
    }
}
